/*
 * fetchWithAuth — shared fetch wrapper for React data services.
 *
 * Problem it solves: profile-update and member-join APIs sometimes return 401
 * (expired token / scope mismatch) mid-flow. The old data services resolved the
 * response body without checking status, so a 401 either silently failed or left
 * the component on an infinite spinner.
 *
 * fetchWithAuth checks the response status. On 401 it:
 *   1. logs the failing endpoint to Application Insights (server-side, via
 *      /api/reactforms/LogClientAuthFailure — there is no client AI SDK),
 *   2. rejects with a recoverable error (err.isAuthError === true) so callers
 *      can stop their own spinners.
 *
 * For all other statuses it resolves the Response unchanged, so it is a drop-in
 * replacement for fetch().
 */

var AUTH_ERROR_STATUS = 401;
var LOG_ENDPOINT = '/api/reactforms/LogClientAuthFailure';
var UNDEFINED_LOG_ENDPOINT = '/api/reactforms/LogClientUndefinedUrl';

// Strip query string and origin so telemetry groups by logical endpoint
// (e.g. "/api/account/updateeducation") rather than per-request URLs.
function toEndpointName(url) {
    if (!url) return 'unknown';
    var name = String(url).split('?')[0];

    var schemeIndex = name.indexOf('://');
    if (schemeIndex >= 0) {
        var pathStart = name.indexOf('/', schemeIndex + 3);
        name = pathStart >= 0 ? name.substring(pathStart) : name;
    }

    return name;
}

function logAuthFailure(endpointName) {
    try {
        fetch(LOG_ENDPOINT, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: 'endpoint=' + encodeURIComponent(endpointName)
        }).catch(function () { /* telemetry must never throw */ });
    } catch (e) { /* no-op */ }
}

/* ------------------------------------------------------------------ *
 * ASAWEB-864: malformed "/undefined" URL guard
 *
 * Several data services build request URLs by interpolating a path or query
 * segment (e.g. `'/api/.../' + id`, `'?productType=' + userInfo.x`). When the
 * interpolated value is undefined the URL literally contains "undefined"
 * ("/meetings/undefined", "?orderNumber=undefined"); the request 404s or hangs
 * and the UI is stuck on a permanent spinner.
 *
 * The guard (a) detects a literal "undefined" URL segment, (b) blocks the
 * request, (c) logs a console.error, and (d) POSTs an Application Insights
 * event so regressions surface immediately. installGlobalUrlGuard() patches
 * window.fetch and XMLHttpRequest so callers we do not own — chiefly Coveo on
 * the ShopASA grid, which never routes through fetchWithAuth — are covered too.
 * ------------------------------------------------------------------ */

// Matches a whole "undefined" token sitting between URL delimiters — the exact
// shape produced when a template path/query segment interpolates an undefined
// value ("/undefined", "=undefined", a trailing "/undefined"). Anchoring to
// delimiters avoids false positives on legitimate substrings such as a search
// for the word "undefined" inside a longer query value, or a path segment like
// "/undefinedbehavior".
var UNDEFINED_URL_SEGMENT = /(?:^|[\/=&?#])undefined(?=$|[\/=&?#])/i;

function urlHasUndefinedSegment(url) {
    if (url === undefined || url === null) return false;
    return UNDEFINED_URL_SEGMENT.test(String(url));
}

function logUndefinedUrl(endpointName, source) {
    try {
        // Same fire-and-forget POST as logAuthFailure. The endpoint carries no
        // "undefined" segment, so the global fetch patch passes it straight
        // through — no recursion.
        fetch(UNDEFINED_LOG_ENDPOINT, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: 'endpoint=' + encodeURIComponent(endpointName) +
                  '&source=' + encodeURIComponent(source || '')
        }).catch(function () { /* telemetry must never throw */ });
    } catch (e) { /* no-op */ }
}

function reportUndefinedUrl(url, source) {
    var endpointName = toEndpointName(url);

    try {
        if (typeof console !== 'undefined' && console.error) {
            console.error(
                '[ASAWEB-864] Blocked request to malformed URL containing "undefined": "' +
                String(url) + '" (via ' + (source || 'unknown') + '). ' +
                'A path or query parameter was undefined when the URL was built.');
        }
    } catch (e) { /* no-op */ }

    logUndefinedUrl(endpointName, source);
}

function makeUndefinedUrlError(url) {
    var error = new Error('Blocked request to malformed URL (contains "undefined"): ' + String(url));
    error.isUndefinedUrlError = true;
    error.url = String(url);
    return error;
}

/**
 * Patch window.fetch and XMLHttpRequest.open once so every request — including
 * ones from code we do not own (Coveo, jQuery $.ajax) — is screened for a
 * literal "undefined" segment before it fires. Idempotent: safe to call from
 * each entry bundle.
 */
function installGlobalUrlGuard() {
    if (typeof window === 'undefined' || window.__asaUrlGuardInstalled) return;
    window.__asaUrlGuardInstalled = true;

    // --- fetch ---
    if (typeof window.fetch === 'function') {
        var wrappedFetch = window.fetch;
        window.fetch = function (input, init) {
            var url = (input && typeof input === 'object' && 'url' in input) ? input.url : input;
            if (urlHasUndefinedSegment(url)) {
                reportUndefinedUrl(url, 'fetch');
                return Promise.reject(makeUndefinedUrlError(url));
            }
            return wrappedFetch.apply(this, arguments);
        };
    }

    // --- XMLHttpRequest (jQuery $.ajax and Coveo both go through XHR) ---
    if (typeof window.XMLHttpRequest === 'function' && window.XMLHttpRequest.prototype &&
        typeof window.XMLHttpRequest.prototype.open === 'function') {
        var nativeOpen = window.XMLHttpRequest.prototype.open;
        window.XMLHttpRequest.prototype.open = function (method, url) {
            if (urlHasUndefinedSegment(url)) {
                reportUndefinedUrl(url, 'xhr');
                // Throwing here aborts before the socket opens, so the request
                // never hangs pending against "/undefined". The feature-level
                // 3s watchdog restores the UI from the spinner.
                throw makeUndefinedUrlError(url);
            }
            return nativeOpen.apply(this, arguments);
        };
    }
}

/**
 * Drop-in fetch replacement that treats HTTP 401 as a recoverable auth error.
 * @param {string} url
 * @param {object} [options] standard fetch options
 * @returns {Promise<Response>} resolves the Response for non-401 responses;
 *          rejects with a recoverable error (isAuthError === true) on 401.
 */
function fetchWithAuth(url, options) {
    if (urlHasUndefinedSegment(url)) {
        reportUndefinedUrl(url, 'fetchWithAuth');
        return Promise.reject(makeUndefinedUrlError(url));
    }

    return fetch(url, options).then(function (response) {
        if (response && response.status === AUTH_ERROR_STATUS) {
            var endpointName = toEndpointName(url);

            logAuthFailure(endpointName);

            var error = new Error('Unauthorized (401) calling ' + endpointName);
            error.isAuthError = true;
            error.status = AUTH_ERROR_STATUS;
            error.endpoint = endpointName;
            throw error;
        }

        return response;
    });
}

/** True when the given rejection came from a 401 surfaced by fetchWithAuth. */
function isAuthError(error) {
    return !!(error && error.isAuthError);
}

/** True when the given rejection came from the /undefined URL guard. */
function isUndefinedUrlError(error) {
    return !!(error && error.isUndefinedUrlError);
}

module.exports = {
    fetchWithAuth: fetchWithAuth,
    isAuthError: isAuthError,
    installGlobalUrlGuard: installGlobalUrlGuard,
    urlHasUndefinedSegment: urlHasUndefinedSegment,
    isUndefinedUrlError: isUndefinedUrlError
};
